import React, { useCallback, useEffect, useState } from "react";
import Dashboard from "../layouts/Dashboard";
import { Card, Col, Row, Table, Tooltip, message, DatePicker } from "antd";
import { Button } from "reactstrap";
import axios from "../services/axios";
import moment from "moment";
import ReactLoading from "react-loading";
import exportFromJSON from "export-from-json";
import isConsultant from "../services/isConsultant";
import { checkPermission } from "../utils/checkPermission";
import useRole from "../hooks/useRole";
import { flushSessionActivity } from "../services/sessionActivityTracker";

const { RangePicker } = DatePicker;
const ACTIVE_SESSION_GRACE_MS = 45000;
const REPORT_REFRESH_INTERVAL_MS = 30000;

//disable date logic -{Lovejot- 23-08-2022}

const disabledDate = (current) => {
  // 90 DAYS GAP AND DISABLE TOMORROW
  return (
    current.isBefore(moment().subtract(90, "day")) ||
    current > moment().endOf("day")
  );
};

const isRecordCurrentlyActive = (record, nowMs = Date.now()) => {
  if (!record) {
    return false;
  }

  if (record.isCurrentlyActive === false || record.isSessionActive === false) {
    return false;
  }

  if (record.sessionLogoutTime && record.isCurrentlyActive !== true) {
    return false;
  }

  const activityAt =
    record.lastActivityAt || record.sessionLiveTime || record.sessionLoginTime;
  if (!activityAt) {
    return false;
  }

  const activityTime = new Date(activityAt).getTime();
  if (Number.isNaN(activityTime)) {
    return false;
  }

  return nowMs - activityTime <= ACTIVE_SESSION_GRACE_MS;
};

const LoginReport = (props) => {
  const [defaultDate, setDefaultDate] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [load, setLoad] = useState(false);
  const [now, setNow] = useState(Date.now());

  const [canViewLoginReport, setCanViewLoginReport] = useState(false);
  const [canDownloadLoginReport, setCanDownloadLoginReport] = useState(false);
  const userRole = useRole();

  const checkPermissions = useCallback(async () => {
    try {
      const viewAllowed = await checkPermission(
        userRole,
        "login_report_module",
        "view_login_report"
      );
      const downloadAllowed = await checkPermission(
        userRole,
        "login_report_module",
        "download_login_report"
      );
      setCanViewLoginReport(viewAllowed);
      setCanDownloadLoginReport(downloadAllowed);
    } catch (err) {
      console.error("Error checking login report permissions:", err);
    }
  }, [userRole]);

  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  const [current, setCurrent] = useState(1);

  const onChange = (page) => {
    console.log(page);
    setCurrent(page);
  };

  const loadCSV = useCallback(async () => {
    try {
      // Flush current user's local pending activity to the server first
      await flushSessionActivity();
    } catch (err) {
      console.error("Error flushing session activity:", err);
    }

    setLoad(true);
    axios
      .get(
        `/reports/loginLogs?StartDate=${
          defaultDate[0]
            ? defaultDate[0]
            : moment(new Date()).format("YYYY-MM-DD")
        }&EndDate=${
          defaultDate[1]
            ? defaultDate[1]
            : moment(new Date()).format("YYYY-MM-DD")
        }`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("user.token")}`,
          },
        }
      )
      .then((res) => {
        //  console.log("RES", res.data);
        setCsvData(res.data);
        setLoad(false);
      })
      .catch((err) => {
        console.log(err);
        message.warning("Error Occured", 2);
        setLoad(false);
      });
  }, [defaultDate]);

  useEffect(() => {
    loadCSV();

    // Auto-polling: Refresh logs and flush current user's activity
    const interval = setInterval(() => {
      loadCSV();
    }, REPORT_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loadCSV]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  //DATE FILTER
  const handleDate = (value, dateString) => {
    console.log("date is ", value, dateString);
    if (value === null) {
      setCurrent(1);
      setDefaultDate([
        moment(new Date()).format("YYYY-MM-DD"),
        moment(new Date()).format("YYYY-MM-DD"),
      ]);
    } else {
      setCurrent(1);
      setDefaultDate(dateString);
    }
  };

  const onOk = (value) => {
    // console.log("onOk:", value);
  };

  const getSessionSeconds = (record, nowMs = Date.now()) => {
    const savedSeconds = Number(
      record?.reportSessionDurationSeconds ??
        record?.reportActiveDurationSeconds ??
        record?.activeDurationSeconds ??
        0
    );

    if (!isRecordCurrentlyActive(record, nowMs)) {
      return savedSeconds;
    }

    const activityAt =
      record?.lastActivityAt || record?.sessionLiveTime || record?.sessionLoginTime;
    const activityTime = new Date(activityAt).getTime();

    if (Number.isNaN(activityTime)) {
      return savedSeconds;
    }

    return savedSeconds + Math.floor(Math.max(0, nowMs - activityTime) / 1000);
  };

  const formatSessionDuration = (totalSeconds) => {
    const normalizedSeconds = Math.max(0, Math.floor(Number(totalSeconds || 0)));
    const hours = Math.floor(normalizedSeconds / 3600);
    const minutes = Math.floor((normalizedSeconds % 3600) / 60);
    const seconds = normalizedSeconds % 60;

    return [hours, minutes, seconds]
      .map((value) => String(value).padStart(2, "0"))
      .join(":");
  };

  //CSV DATA
  const exportType = "csv";
  let fileName = `LOGIN_REPORT_${moment(new Date()).format(
    "DD-MM-YYYY_HH:mm"
  )}`;
  const downloadCSV = async () => {
    try {
      // Flush current user's local pending activity to the server first
      await flushSessionActivity();
    } catch (err) {
      console.error("Error flushing session activity:", err);
    }

    setLoad(true);
    try {
      const res = await axios.get(
        `/reports/loginLogs?StartDate=${
          defaultDate[0]
            ? defaultDate[0]
            : moment(new Date()).format("YYYY-MM-DD")
        }&EndDate=${
          defaultDate[1]
            ? defaultDate[1]
            : moment(new Date()).format("YYYY-MM-DD")
        }`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("user.token")}`,
          },
        }
      );
      
      setCsvData(res.data);

      let data = res.data?.map((ele) => {
        return {
          "User Name": ele?.name,
          "Email": ele?.email,
          "Employee ID": ele?.employeeId,
          "Role": ele?.role,
          "Login Time": ele?.sessionLoginTime ? moment(ele.sessionLoginTime).format("DD-MM-YYYY HH:mm:ss") : "-",
          "Session Time": formatSessionDuration(getSessionSeconds(ele, Date.now())),
          "IP Address": ele?.ipAddress,
          "User Agent": ele?.userAgent,
        };
      });
      setCurrent(1);
      exportFromJSON({ data, fileName, exportType });
    } catch (err) {
      console.error(err);
      message.warning("Error Occured during download", 2);
    } finally {
      setLoad(false);
    }
  };

  const columns = [
    {
      title: "User Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 600, color: "#1890ff" }}>{text || "-"}</span>,
      fixed: 'left',
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => text || "-",
    },
    {
      title: "Employee ID",
      dataIndex: "employeeId",
      key: "employeeId",
      render: (text) => text || "-",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text) => (
        <span className="badge badge-info" style={{ textTransform: 'capitalize' }}>
          {text?.replace('_', ' ') || "-"}
        </span>
      ),
    },
    {
      title: "Login Time",
      dataIndex: "sessionLoginTime",
      key: "sessionLoginTime",
      render: (text) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {text ? moment(text).format("DD-MM-YYYY HH:mm:ss") : "-"}
        </span>
      ),
      sorter: (a, b) => new Date(a.sessionLoginTime) - new Date(b.sessionLoginTime),
    },
    {
      title: "Session Time",
      dataIndex: "reportActiveDurationSeconds",
      key: "sessionTime",
      render: (_, record) => (
        <span style={{ whiteSpace: 'nowrap', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {formatSessionDuration(getSessionSeconds(record, now))}
          {isRecordCurrentlyActive(record, now) && (
            <span 
              className="active-dot" 
              style={{
                width: '8px',
                height: '8px',
                backgroundColor: '#52c41a',
                borderRadius: '50%',
                display: 'inline-block',
                boxShadow: '0 0 8px #52c41a',
                animation: 'pulse 1.5s infinite'
              }}
            />
          )}
        </span>
      ),
      sorter: (a, b) => getSessionSeconds(a, now) - getSessionSeconds(b, now),
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      key: "ipAddress",
      render: (text) => <code style={{ color: "#e83e8c" }}>{text || "-"}</code>,
    },
    {
      title: "User Agent",
      dataIndex: "userAgent",
      key: "userAgent",
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          <span style={{ cursor: 'help' }}>
            {text && text.length > 50 ? `${text.substring(0, 50)}...` : text}
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {!isConsultant() && (
        <Dashboard title="Login Logs">
          {load && (
            <div className="spinner-overlay" style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(255,255,255,0.7)',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <ReactLoading type="bars" color="#ff9800" height={50} width={50} />
            </div>
          )}

          <Card className="mb-4 shadow-sm border-0" bodyStyle={{ padding: '16px' }}>
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col xs={24} md={18}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontWeight: 500, color: '#595959' }}>Select Period:</span>
                  <RangePicker
                    style={{ width: '100%', maxWidth: '400px' }}
                    format="YYYY-MM-DD"
                    defaultValue={[moment(), moment()]}
                    onChange={handleDate}
                    onOk={onOk}
                    disabledDate={disabledDate}
                    placeholder={[
                      moment(new Date()).format("YYYY-MM-DD"),
                      moment(new Date()).format("YYYY-MM-DD"),
                    ]}
                  />
                </div>
              </Col>
              <Col xs={24} md={6} style={{ textAlign: 'right' }}>
                {canDownloadLoginReport && (
                  <Button 
                    className="btn-orange"
                    style={{ 
                      backgroundColor: '#ff9800', 
                      borderColor: '#ff9800', 
                      color: '#fff',
                      padding: '8px 24px',
                      borderRadius: '6px',
                      fontWeight: 600,
                      boxShadow: '0 4px 6px rgba(255, 152, 0, 0.2)',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 12px rgba(255, 152, 0, 0.3)'; }}
                    onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 6px rgba(255, 152, 0, 0.2)'; }}
                    onClick={downloadCSV}
                  >
                    <i className="fas fa-download mr-2"></i> Download CSV
                  </Button>
                )}
              </Col>
            </Row>
          </Card>

          {canViewLoginReport ? (
            <Card className="shadow-sm border-0" bodyStyle={{ padding: 0 }}>
              <Table
                columns={columns}
                dataSource={csvData}
                loading={load}
                rowKey="_id"
                pagination={{
                  current: current,
                  pageSize: 10,
                  total: csvData?.length || 0,
                  onChange: onChange,
                  showSizeChanger: false,
                  showTotal: (total) => `Total ${total} logs`,
                  position: ['bottomRight']
                }}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No login logs found for the selected period' }}
                className="custom-table"
              />
            </Card>
          ) : (
            !load && (
              <Card className="p-5 text-center">
                <i className="fas fa-lock mb-3" style={{ fontSize: '48px', color: '#ff4d4f' }}></i>
                <h3>Access Denied</h3>
                <p className="text-muted">You are not authorized to view this report. Please contact your administrator if you believe this is an error.</p>
              </Card>
            )
          )}
        </Dashboard>
      )}
      <style>{`
        .custom-table .ant-table-thead > tr > th {
          background-color: #fafafa;
          font-weight: 700;
          color: #262626;
        }
        .custom-table .ant-table-tbody > tr:hover > td {
          background-color: #fff7e6 !important;
        }
        .ant-picker:hover, .ant-picker-focused {
          border-color: #ff9800 !important;
        }
        .ant-picker-range-separator {
          color: #ff9800;
        }
        .ant-pagination-item-active {
          border-color: #ff9800 !important;
        }
        .ant-pagination-item-active a {
          color: #ff9800 !important;
        }
        @keyframes pulse {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7);
          }
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 6px rgba(82, 196, 26, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(82, 196, 26, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default LoginReport;
